/**
 * Thin Atlas → scene.v1 bridge.
 *
 * Parses Obsidian Atlas VTT `.atlasmap` envelopes (ByteMirror/atlas-vtt) into
 * Path A `scene.v1` documents. No Atlas plugin source is copied here — only
 * the on-disk JSON shape we observed from the vault + upstream types.
 */

import { parseAtlasFog } from "./fog";
import { resolvePinVisibility } from "./pinVisibility";
import { parseAtlasWidgets } from "./widgets";
import {
  DEFAULT_MAP_HEIGHT,
  DEFAULT_MAP_WIDTH,
  MAX_PINS,
  TOKEN_SIZE,
  parseSceneV1,
  type ParsedPinSeed,
  type ParsedSceneSeed,
  type SceneGrid,
  type SceneV1Document,
} from "./sceneV1";

export const ATLAS_SCHEMA = "atlas-vtt" as const;

export type AtlasBridgeOptions = {
  /** Scene id override (default: derived from mapPath / name). */
  id?: string;
  /** Scene display name override. */
  name?: string;
  /**
   * Optional HTTP(S) base prepended to vault-relative art paths.
   * Example: temporary Dropbox shared-folder URL prefix.
   */
  assetBaseUrl?: string;
  /**
   * Exact vault-path → public URL rewrites (wins over assetBaseUrl).
   * Useful for local fixtures and known demo stand-ins.
   */
  pathRewrites?: Record<string, string>;
  mapWidth?: number;
  mapHeight?: number;
};

export type AtlasBridgeResult =
  | { ok: true; scene: SceneV1Document; seed: ParsedSceneSeed }
  | { ok: false; error: string };

type AtlasTokenLike = {
  id?: unknown;
  name?: unknown;
  imagePath?: unknown;
  x?: unknown;
  y?: unknown;
  kind?: unknown;
  isHidden?: unknown;
  size?: unknown;
  scale?: unknown;
  showRing?: unknown;
  ringColor?: unknown;
  showNameplate?: unknown;
  hp?: unknown;
  stress?: unknown;
  maxStress?: unknown;
  ownerKey?: unknown;
  notePath?: unknown;
  statblockPath?: unknown;
};

type AtlasPinLike = {
  id?: unknown;
  name?: unknown;
  label?: unknown;
  x?: unknown;
  y?: unknown;
  notePath?: unknown;
  gmOnly?: unknown;
  visibility?: unknown;
  visibleTo?: unknown;
  visibleToPlayer?: unknown;
  icon?: unknown;
  kind?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Detect Zustand persist envelope `{ version, state }` used by Atlas maps. */
export function unwrapAtlasEnvelope(raw: unknown): unknown {
  if (!isRecord(raw)) return raw;
  if (isRecord(raw.state) && (raw.version === undefined || typeof raw.version === "number")) {
    return raw.state;
  }
  return raw;
}

export function looksLikeAtlasMap(raw: unknown): boolean {
  const state = unwrapAtlasEnvelope(raw);
  if (!isRecord(state)) return false;
  if (state.schema === ATLAS_SCHEMA) return true;
  if (typeof state.background === "string" || state.background === null) {
    if (isRecord(state.objects) && isRecord(state.objects.tokens)) return true;
  }
  return false;
}

function slugify(input: string): string {
  const slug = input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return slug || "atlas-scene";
}

function resolveArtPath(vaultPath: string, options: AtlasBridgeOptions): string {
  const trimmed = vaultPath.trim();
  if (!trimmed) return trimmed;
  if (/^(https?:|data:|blob:|\/)/i.test(trimmed)) return trimmed;

  const rewrite = options.pathRewrites?.[trimmed];
  if (rewrite) return rewrite;

  const base = options.assetBaseUrl?.trim();
  if (!base) return trimmed;

  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const normalizedPath = trimmed.replace(/^\/+/, "");
  return `${normalizedBase}/${normalizedPath}`;
}

function tokenDisplayName(token: AtlasTokenLike, fallbackId: string): string {
  if (typeof token.name === "string" && token.name.trim()) return token.name.trim();
  if (typeof token.imagePath === "string" && token.imagePath.trim()) {
    const leaf = token.imagePath.split("/").pop() ?? fallbackId;
    return leaf.replace(/\.[^.]+$/, "") || fallbackId;
  }
  return fallbackId;
}

function atlasTokenBar(
  raw: unknown,
  fallbackMax: number,
  maxStressFallback?: unknown,
): { current: number; max: number } | null {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const max =
      typeof maxStressFallback === "number" &&
      Number.isFinite(maxStressFallback) &&
      maxStressFallback > 0
        ? maxStressFallback
        : fallbackMax;
    return { current: Math.max(0, raw), max: Math.max(1, max) };
  }
  if (typeof raw !== "object") return null;
  const bag = raw as Record<string, unknown>;
  if (
    typeof bag.current !== "number" ||
    !Number.isFinite(bag.current) ||
    typeof bag.max !== "number" ||
    !Number.isFinite(bag.max) ||
    bag.max <= 0
  ) {
    return null;
  }
  return { current: Math.max(0, bag.current), max: bag.max };
}

/**
 * Convert an Atlas `.atlasmap` JSON value into a Path A scene.v1 document.
 * Hidden tokens are kept with gmOnly so the GM board can dim them; the server
 * strips them from player snapshots.
 */
export function atlasMapToSceneV1(
  raw: unknown,
  options: AtlasBridgeOptions = {},
): AtlasBridgeResult {
  const state = unwrapAtlasEnvelope(raw);
  if (!isRecord(state)) {
    return { ok: false, error: "Atlas map must be a JSON object" };
  }
  if (!looksLikeAtlasMap(raw)) {
    return {
      ok: false,
      error: 'Not an Atlas .atlasmap (expected schema "atlas-vtt" or objects.tokens)',
    };
  }

  const background =
    typeof state.background === "string" && state.background.trim()
      ? state.background.trim()
      : null;
  if (!background) {
    return { ok: false, error: "Atlas scene has no background map image" };
  }

  const objects = isRecord(state.objects) ? state.objects : {};
  const tokensRaw = isRecord(objects.tokens) ? objects.tokens : {};

  const mapPath =
    typeof state.mapPath === "string" && state.mapPath.trim()
      ? state.mapPath.trim()
      : null;
  const atlasName =
    typeof state.name === "string" && state.name.trim()
      ? state.name.trim()
      : mapPath
        ? (mapPath.split("/").pop() ?? "Atlas scene").replace(/\.atlasmap$/i, "")
        : "Atlas scene";

  const sceneId = (options.id?.trim() || slugify(atlasName)).slice(0, 64);
  const sceneName = (options.name?.trim() || atlasName).slice(0, 120);

  const grid = isRecord(state.grid) ? state.grid : {};
  const gridSize =
    typeof grid.size === "number" && Number.isFinite(grid.size) && grid.size > 0
      ? grid.size
      : TOKEN_SIZE;

  let sceneGrid: SceneGrid | null = null;
  if (isRecord(state.grid)) {
    sceneGrid = {
      enabled: state.grid.enabled !== false,
      visible: state.grid.visible !== false,
      type: typeof state.grid.type === "string" ? state.grid.type : "square",
      size: gridSize,
      offsetX: typeof state.grid.offsetX === "number" ? state.grid.offsetX : 0,
      offsetY: typeof state.grid.offsetY === "number" ? state.grid.offsetY : 0,
      opacity: typeof state.grid.opacity === "number" ? state.grid.opacity : 0.4,
      color: typeof state.grid.color === "string" ? state.grid.color : "rgba(255, 255, 255, 0.4)",
      lineType: typeof state.grid.lineType === "string" ? state.grid.lineType : "solid",
      lineWidth: typeof state.grid.lineWidth === "number" ? state.grid.lineWidth : 1,
    };
  }

  const tokens: SceneV1Document["tokens"] = [];
  const seenTokenIds = new Set<string>();
  for (const [key, value] of Object.entries(tokensRaw)) {
    if (!isRecord(value)) {
      return { ok: false, error: `Token ${key} is not an object` };
    }
    const token = value as AtlasTokenLike;
    const id =
      typeof token.id === "string" && token.id.trim() ? token.id.trim() : key;
    if (seenTokenIds.has(id)) continue;
    seenTokenIds.add(id);

    const rawImage = typeof token.imagePath === "string" ? token.imagePath.trim() : "";
    const hasPortrait = rawImage.length > 0 && rawImage !== "atlas-vtt/assets/character.png";
    const imagePath = hasPortrait ? rawImage : "/demo-adventure/token-rowan.svg";

    if (typeof token.x !== "number" || !Number.isFinite(token.x)) {
      return { ok: false, error: `Token ${id} missing numeric x` };
    }
    if (typeof token.y !== "number" || !Number.isFinite(token.y)) {
      return { ok: false, error: `Token ${id} missing numeric y` };
    }

    const scale =
      typeof token.scale === "number" && Number.isFinite(token.scale) && token.scale > 0
        ? token.scale
        : 1;
    const rawSize =
      typeof token.size === "number" && Number.isFinite(token.size) && token.size > 0
        ? token.size
        : 1;
    // In Atlas VTT, a person/standard token is 1 grid square (gridSize).
    // If rawSize <= 10 it's a grid multiplier (1 = 1 square, 2 = 2x2, etc.)
    const explicitSize =
      rawSize <= 10
        ? gridSize * rawSize * scale
        : Math.max(gridSize, rawSize * scale);

    const ringColorRaw =
      typeof token.ringColor === "string" && token.ringColor.trim()
        ? token.ringColor.trim().slice(0, 32)
        : null;
    // Atlas ring when enabled / coloured; nameplated tokens without a ring still
    // get parchment chrome so adversary pieces match Obsidian (e.g. Marsh Assassin).
    let showRing =
      token.showRing === true ||
      (token.showRing !== false && ringColorRaw !== null);
    let ringColor = ringColorRaw;
    const showNameplate = token.showNameplate === true;
    if (!showRing && showNameplate) {
      showRing = true;
      ringColor = "#C4A574";
    }
    const hp = atlasTokenBar(token.hp, 100);
    const stress = atlasTokenBar(token.stress, 10, token.maxStress);

    // Tokens must be about one grid square, not a tiny speck. Minimum is gridSize (default 70px).
    const tokenPixelSize = Math.max(gridSize, Math.min(1024, explicitSize));

    const notePathRaw =
      (typeof token.statblockPath === "string" && token.statblockPath.trim()
        ? token.statblockPath.trim()
        : null) ||
      (typeof token.notePath === "string" && token.notePath.trim()
        ? token.notePath.trim()
        : null);
    tokens.push({
      id,
      name: tokenDisplayName(token, id),
      artUrl: resolveArtPath(imagePath, options),
      x: token.x,
      y: token.y,
      ownerKey:
        typeof token.ownerKey === "string" && token.ownerKey.trim()
          ? token.ownerKey.trim()
          : null,
      gmOnly: token.isHidden === true,
      size: tokenPixelSize,
      showRing,
      ringColor,
      showNameplate,
      hp,
      stress,
      notePath: notePathRaw,
    });
  }

  if (tokens.length > 64) {
    return { ok: false, error: "Too many tokens after bridge (max 64)" };
  }

  const pinsRaw = isRecord(objects.pins) ? objects.pins : {};
  const pins: ParsedPinSeed[] = [];
  const seenPinIds = new Set<string>();
  for (const [key, value] of Object.entries(pinsRaw)) {
    if (!isRecord(value)) continue;
    const pin = value as AtlasPinLike;
    const id =
      typeof pin.id === "string" && pin.id.trim() ? pin.id.trim() : key.trim();
    if (!id || seenPinIds.has(id)) continue;
    if (typeof pin.x !== "number" || !Number.isFinite(pin.x)) continue;
    if (typeof pin.y !== "number" || !Number.isFinite(pin.y)) continue;
    seenPinIds.add(id);
    const labelFromName =
      typeof pin.name === "string" && pin.name.trim() ? pin.name.trim() : null;
    const labelFromLabel =
      typeof pin.label === "string" && pin.label.trim()
        ? pin.label.trim()
        : null;
    const label = (labelFromLabel || labelFromName || id).slice(0, 120);
    const notePath =
      typeof pin.notePath === "string" && pin.notePath.trim()
        ? pin.notePath.trim()
        : null;
    const vis = resolvePinVisibility(pin);
    const icon =
      typeof pin.icon === "string" && pin.icon.trim()
        ? pin.icon.trim().slice(0, 32)
        : null;
    pins.push({
      id,
      x: pin.x,
      y: pin.y,
      label,
      notePath,
      icon,
      gmOnly: vis.gmOnly,
      visibility: vis.visibility,
      visibleTo: vis.visibleTo,
      content: null,
    });
  }
  if (pins.length > MAX_PINS) {
    return { ok: false, error: `Too many pins after bridge (max ${MAX_PINS})` };
  }

  const fogRaw = isRecord(objects.fog) ? objects.fog : {};
  const fog = parseAtlasFog(fogRaw);
  const widgets = parseAtlasWidgets(
    state.widgetSettings,
    state.widgetValues,
    (isRecord(objects) ? (objects as Record<string, unknown>).widgets : undefined) ?? state.widgets,
  );

  const doc: SceneV1Document = {
    schemaVersion: "scene.v1",
    id: sceneId,
    name: sceneName,
    map: {
      url: resolveArtPath(background, options),
      width: options.mapWidth ?? DEFAULT_MAP_WIDTH,
      height: options.mapHeight ?? DEFAULT_MAP_HEIGHT,
      ...(sceneGrid ? { grid: sceneGrid } : {}),
    },
    tokens,
    pins: pins.map((p) => ({
      id: p.id,
      x: p.x,
      y: p.y,
      label: p.label,
      notePath: p.notePath,
      icon: p.icon,
      gmOnly: p.gmOnly,
      visibility: p.visibility,
      visibleTo: p.visibleTo,
    })),
    fog,
    widgets,
  };

  const parsed = parseSceneV1(doc);
  if (!parsed.ok) {
    return { ok: false, error: `Bridge produced invalid scene.v1: ${parsed.error}` };
  }

  return { ok: true, scene: doc, seed: parsed.scene };
}

/**
 * Accept either scene.v1 or Atlas .atlasmap and normalize to scene.v1.
 */
export function coerceToSceneV1(
  raw: unknown,
  options: AtlasBridgeOptions = {},
): AtlasBridgeResult {
  if (isRecord(raw) && raw.schemaVersion === "scene.v1") {
    const parsed = parseSceneV1(raw);
    if (!parsed.ok) return { ok: false, error: parsed.error };
    return {
      ok: true,
      scene: raw as unknown as SceneV1Document,
      seed: parsed.scene,
    };
  }
  return atlasMapToSceneV1(raw, options);
}
